'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _slicedToArray = _interopDefault(require('@babel/runtime/helpers/slicedToArray'));
var React = require('react');
var React__default = _interopDefault(React);
var reactStripeElements = require('react-stripe-elements');
var components = require('@engaging-tech/components');
var _extends = _interopDefault(require('@babel/runtime/helpers/extends'));
var styled = _interopDefault(require('styled-components'));
var _asyncToGenerator = _interopDefault(require('@babel/runtime/helpers/asyncToGenerator'));
var _objectWithoutProperties = _interopDefault(require('@babel/runtime/helpers/objectWithoutProperties'));
var _regeneratorRuntime = _interopDefault(require('@babel/runtime/regenerator'));
var frontendUtils = require('@engaging-tech/frontend-utils');
var formik = require('formik');
var yup = require('yup');

/**
 * A wrapper element for Stripe Elements that lazy loads the stripe JS library. This
 * file has to be loaded in this way to ensure PCI compliancy for online payments.
 * @param {string} key The API key for stripe. Defaults to whatever is specified
 * in config.keys.stripe
 */
var StripeWrapper = function StripeWrapper(_ref) {
  var apiKey = _ref.apiKey,
    children = _ref.children;
  var _useState = React.useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    stripe = _useState2[0],
    setStripe = _useState2[1];
  var isInitialised = !!(window && window.Stripe);
  var remove = function remove() {
    if (document.getElementById("stripe-js")) {
      document.getElementById("stripe-js").remove();
    }
    window.Stripe = null;
  };
  var createScript = function createScript() {
    var stripeJs = document.createElement("script");
    stripeJs.id = "stripe-js";
    stripeJs.src = "https://js.stripe.com/v3/";
    stripeJs.async = true;
    return stripeJs;
  };
  var initialise = function initialise() {
    if (isInitialised) {
      remove(); // Ensure there's only *one* copy of stripe.js on the page at any time.
    }

    var stripeJs = createScript();
    stripeJs.onload = function () {
      setStripe(window.Stripe(apiKey));
    };
    if (document.body) {
      document.body.appendChild(stripeJs);
    }
    return function () {
      remove();
    };
  };
  React.useEffect(function () {
    return initialise();
  }, []);
  return stripe ? /*#__PURE__*/React__default.createElement(reactStripeElements.StripeProvider, {
    stripe: stripe
  }, /*#__PURE__*/React__default.createElement(reactStripeElements.Elements, null, children)) : /*#__PURE__*/React__default.createElement(components.Spinner, {
    color: "primary.0",
    justifyContent: "center"
  });
};

var PlaceholderInput = styled.input.withConfig({
  displayName: "CardDetails__PlaceholderInput",
  componentId: "sc-3eonzm-0"
})(["font-size:16px;border:none;background-color:transparent;width:100%;padding-bottom:8px;"]);
var CardInputBox = styled(components.Box).withConfig({
  displayName: "CardDetails__CardInputBox",
  componentId: "sc-3eonzm-1"
})(["position:relative;margin-top:8px;background-color:", ";border-bottom:", ";padding:", ";border-radius:4px 4px 0 0;transition:0.15s;:hover{background-color:", ";border-bottom:", ";}label{color:", ";font-family:", ";left:16px;right:auto;top:18px;pointer-events:none;position:absolute;line-height:1.15rem;transition:0.15s;transform:", ";}"], function (_ref) {
  var theme = _ref.theme;
  return theme.colors.dark[7];
}, function (_ref2) {
  var theme = _ref2.theme,
    isTouched = _ref2.isTouched,
    hasError = _ref2.hasError;
  if (hasError) {
    return "2px solid ".concat(theme.colors.error[0]);
  }
  if (isTouched) {
    return "2px solid ".concat(theme.colors.primary[0]);
  }
  return "1px solid ".concat(theme.colors.dark[3]);
}, function (_ref3) {
  var isTouched = _ref3.isTouched;
  return isTouched ? "27px 16px 6px" : "20px 16px 6px";
}, function (_ref4) {
  var theme = _ref4.theme;
  return theme.colors.dark[6];
}, function (_ref5) {
  var theme = _ref5.theme,
    isTouched = _ref5.isTouched;
  return isTouched ? "2px solid ".concat(theme.colors.primary[0]) : "1px solid ".concat(theme.colors.dark[1]);
}, function (_ref6) {
  var theme = _ref6.theme,
    isTouched = _ref6.isTouched,
    hasError = _ref6.hasError;
  if (hasError) {
    return theme.colors.error[0];
  }
  if (isTouched) {
    return theme.colors.primary[0];
  }
  return theme.colors.dark[2];
}, function (_ref7) {
  var theme = _ref7.theme;
  return theme.fonts.sansSerif;
}, function (_ref8) {
  var isTouched = _ref8.isTouched;
  return isTouched ? "translateY(-50%) translateX(-15%) scale(0.75)" : "none";
});
var style = {
  style: {
    base: {
      fontSize: "16px",
      color: "rgba(0,0,0,0.87)",
      "::placeholder": {
        color: "rgba(0,0,0,0.6)"
      }
    },
    invalid: {
      color: "rgba(191,70,48,1)"
    }
  }
};

/* eslint-disable jsx-a11y/label-has-associated-control */
var CardDetails = function CardDetails(_ref9) {
  var onChange = _ref9.onChange;
  var _useState = React.useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isTouched = _useState2[0],
    setIsTouched = _useState2[1];
  var _useState3 = React.useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  var handleChange = function handleChange(e) {
    setError(e.error);
    onChange(e);
  };
  return /*#__PURE__*/React__default.createElement(components.Box, {
    onClick: function onClick() {
      return setIsTouched(true);
    },
    onFocus: function onFocus() {
      return setIsTouched(true);
    }
  }, /*#__PURE__*/React__default.createElement(CardInputBox, {
    isTouched: isTouched,
    hasError: !!error
  }, /*#__PURE__*/React__default.createElement("label", null, "Card details"), isTouched ? /*#__PURE__*/React__default.createElement(reactStripeElements.CardElement, _extends({}, style, {
    hidePostalCode: true,
    onChange: handleChange,
    onReady: function onReady(el) {
      return el.focus();
    }
  })) : /*#__PURE__*/React__default.createElement(PlaceholderInput, {
    type: "text"
  })), /*#__PURE__*/React__default.createElement(components.Box, null, /*#__PURE__*/React__default.createElement(components.Text, {
    fontSize: 2,
    color: "error.0"
  }, error && error.message)));
};

var _excluded = ["text", "children", "color"],
  _excluded2 = ["text", "children", "color"];
var ButtonText = styled(components.Text).withConfig({
  displayName: "BillingForm__ButtonText",
  componentId: "sc-19vgpfv-0"
})(["font-size:14px;font-weight:700;font-family:\"Centra Bold\";"]);
var StyledButton = styled(components.Button).withConfig({
  displayName: "BillingForm__StyledButton",
  componentId: "sc-19vgpfv-1"
})(["&:hover{box-shadow:", ";transform:", ";}"], function (_ref) {
  var theme = _ref.theme;
  return theme.elevations[1];
}, function (_ref2) {
  var disabled = _ref2.disabled;
  return disabled ? "translateY(0px)" : "translateY(0.5px)";
});
var TextButton = function TextButton(_ref3) {
  var _ref3$text = _ref3.text,
    text = _ref3$text === void 0 ? null : _ref3$text,
    children = _ref3.children,
    color = _ref3.color,
    props = _objectWithoutProperties(_ref3, _excluded);
  return /*#__PURE__*/React__default.createElement(components.Button, _extends({
    alignItems: "center",
    width: "auto",
    elevation: 0,
    bg: "transparent"
  }, props), /*#__PURE__*/React__default.createElement(ButtonText, {
    color: color
  }, children || text));
};
var PrimaryButton = function PrimaryButton(_ref4) {
  var _ref4$text = _ref4.text,
    text = _ref4$text === void 0 ? null : _ref4$text,
    _ref4$children = _ref4.children,
    children = _ref4$children === void 0 ? null : _ref4$children,
    _ref4$color = _ref4.color,
    color = _ref4$color === void 0 ? "light.0" : _ref4$color,
    props = _objectWithoutProperties(_ref4, _excluded2);
  return /*#__PURE__*/React__default.createElement(StyledButton, _extends({
    alignItems: "center",
    width: "auto",
    bg: "#013f4b"
  }, props), /*#__PURE__*/React__default.createElement(ButtonText, {
    color: color,
    width: "auto"
  }, children || text));
};

/**
 * A form with a visibility toggle. We need to use this because Stripe
 * will break if we use conditional rendering.
 */
var HidableForm = styled.form.withConfig({
  displayName: "BillingForm__HidableForm",
  componentId: "sc-19vgpfv-2"
})(["display:", ";width:100%;"], function (_ref5) {
  var isVisible = _ref5.isVisible;
  return isVisible ? "block" : "none ";
});
var Form = function Form(_ref6) {
  var handleSubmit = _ref6.handleSubmit,
    errors = _ref6.errors,
    touched = _ref6.touched,
    values = _ref6.values,
    handleChange = _ref6.handleChange,
    handleBlur = _ref6.handleBlur,
    isLoading = _ref6.isLoading,
    isValid = _ref6.isValid,
    submitContent = _ref6.submitContent,
    children = _ref6.children,
    cancelContent = _ref6.cancelContent,
    helpContent = _ref6.helpContent;
  var _useState = React.useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    error = _useState2[0],
    setError = _useState2[1];
  var device = components.CustomHooks.useClientDevice();
  var _useState3 = React.useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isComplete = _useState4[0],
    setIsComplete = _useState4[1];
  var countries = frontendUtils.getCountries();
  var handleStripeChange = function handleStripeChange(_ref7) {
    var stripeError = _ref7.error,
      complete = _ref7.complete;
    setIsComplete(complete);
    setError(stripeError ? stripeError.message : null);
  };
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, isLoading && /*#__PURE__*/React__default.createElement(components.Spinner, {
    color: "primary.0",
    justifyContent: "center"
  }), /*#__PURE__*/React__default.createElement(HidableForm, {
    onSubmit: handleSubmit,
    isVisible: !isLoading
  }, /*#__PURE__*/React__default.createElement(components.Box, {
    mb: 2
  }, /*#__PURE__*/React__default.createElement(components.TextField, {
    label: "Cardholder's Name",
    width: 1 / 1,
    name: "cardholderName",
    id: "cardholder-name",
    borderRadius: "4px 4px 0px 0px",
    type: "text",
    value: values.cardholderName,
    error: touched.cardholderName && errors.cardholderName,
    onChange: handleChange,
    onBlur: handleBlur
  })), /*#__PURE__*/React__default.createElement(components.Box, {
    mb: 2
  }, /*#__PURE__*/React__default.createElement(components.TextField, {
    label: "Email Address",
    width: 1 / 1,
    borderRadius: "4px 4px 0px 0px",
    name: "emailAddress",
    id: "email-address",
    type: "email",
    value: values.emailAddress,
    error: touched.emailAddress && errors.emailAddress,
    onChange: handleChange,
    onBlur: handleBlur
  })), /*#__PURE__*/React__default.createElement(components.Box, {
    mb: 2
  }, /*#__PURE__*/React__default.createElement(components.TextField, {
    label: "Address - first line",
    width: 1 / 1,
    name: "addressFirstLine",
    borderRadius: "4px 4px 0px 0px",
    id: "address-first-line",
    type: "text",
    value: values.addressFirstLine,
    error: touched.addressFirstLine && errors.addressFirstLine,
    onChange: handleChange,
    onBlur: handleBlur
  })), /*#__PURE__*/React__default.createElement(components.Box, {
    mb: 2
  }, /*#__PURE__*/React__default.createElement(components.TextField, {
    label: "Address - second line",
    width: 1 / 1,
    borderRadius: "4px 4px 0px 0px",
    name: "addressSecondLine",
    id: "address-second-line",
    helperText: "Optional",
    type: "text",
    value: values.addressSecondLine,
    error: touched.addressSecondLine && errors.addressSecondLine,
    onChange: handleChange,
    onBlur: handleBlur
  })), /*#__PURE__*/React__default.createElement(components.Flex, {
    mb: 2,
    justifyContent: "space-between",
    flexDirection: ["column", "row"]
  }, /*#__PURE__*/React__default.createElement(components.TextField, {
    label: "Post code",
    wrapperProps: {
      width: device === "MOBILE" ? "100%" : "48%"
    },
    borderRadius: "4px 4px 0px 0px",
    name: "postcode",
    id: "postcode",
    type: "text",
    value: values.postcode,
    error: touched.postcode && errors.postcode,
    onChange: handleChange,
    onBlur: handleBlur
  }), /*#__PURE__*/React__default.createElement(components.TextField, {
    label: "City",
    wrapperProps: {
      width: device === "MOBILE" ? "100%" : "48%"
    },
    name: "city",
    borderRadius: "4px 4px 0px 0px",
    id: "city",
    type: "text",
    value: values.city,
    error: touched.city && errors.city,
    onChange: handleChange,
    onBlur: handleBlur
  })), /*#__PURE__*/React__default.createElement(components.Box, {
    mb: 26
  }, /*#__PURE__*/React__default.createElement(components.Select, {
    name: "country",
    id: "country",
    onChange: handleChange,
    onBlur: handleBlur,
    value: values.country,
    label: "Country"
  }, countries.map(function (country) {
    return /*#__PURE__*/React__default.createElement("option", {
      key: country.code,
      value: country.code
    }, country.name);
  }))), /*#__PURE__*/React__default.createElement(components.Box, {
    mb: 2
  }, /*#__PURE__*/React__default.createElement(CardDetails, {
    onChange: handleStripeChange
  })), helpContent || /*#__PURE__*/React__default.createElement(components.Text, {
    mt: 1,
    mb: 3,
    fontSize: 3,
    color: "dark.2"
  }, "If you need any help with your purchase, please get in touch with our team at:", " ", /*#__PURE__*/React__default.createElement("a", {
    textDecoration: "none",
    color: "primary.0",
    href: "mailto:support@workl.co"
  }, /*#__PURE__*/React__default.createElement(components.Text, {
    color: "primary.0",
    fontSize: 3
  }, "support@workl.co"))), children, /*#__PURE__*/React__default.createElement(components.Flex, {
    width: 1 / 1,
    flexDirection: "row-reverse",
    alignItems: "center",
    justifyContent: "space-between",
    mt: 3
  }, /*#__PURE__*/React__default.createElement(PrimaryButton, {
    type: "submit",
    disabled: !isValid || !isComplete || error || isLoading,
    bg: !isValid || !isComplete || error || isLoading ? "primary.2" : "primary.0",
    ml: 2,
    width: "auto",
    p: 20
  }, submitContent.text), /*#__PURE__*/React__default.createElement(TextButton, {
    width: "auto",
    elevation: 0,
    onClick: cancelContent.action,
    color: "primary.0"
  }, cancelContent.text))));
};
var validation = yup.object().shape({
  cardholderName: yup.string("Please enter the name on the card").required("A cardholder name is required"),
  emailAddress: yup.string("Please enter your email address").email("Please enter a valid email address").required("An email address is required"),
  addressFirstLine: yup.string("Please enter your address").required("An address is required"),
  addressSecondLine: yup.string(),
  postcode: yup.string("Please enter your postal code").required("A valid postal code is required"),
  city: yup.string("Please enter your town or city").required("A valid town or city is required"),
  country: yup.string().required()
});
var initialValues = {
  cardholderName: "",
  emailAddress: "",
  addressFirstLine: "",
  addressSecondLine: "",
  postcode: "",
  city: "",
  country: "GB"
};
var BillingForm = function BillingForm(_ref8) {
  var stripe = _ref8.stripe,
    onSubmit = _ref8.onSubmit,
    onCancel = _ref8.onCancel,
    isLoading = _ref8.isLoading,
    paymentAction = _ref8.paymentAction,
    onPaymentActionSuccess = _ref8.onPaymentActionSuccess,
    onPaymentActionFail = _ref8.onPaymentActionFail,
    onStripeLoadingUpdate = _ref8.onStripeLoadingUpdate,
    children = _ref8.children;
  var _useState5 = React.useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isStripeLoading = _useState6[0],
    setIsStripeLoading = _useState6[1];
  var handleSubmit = /*#__PURE__*/function () {
    var _ref9 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(cardDetails) {
      var token;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              setIsStripeLoading(true);
              _context.next = 3;
              return stripe.createSource({
                type: "card",
                owner: {
                  name: cardDetails.cardholderName,
                  email: cardDetails.emailAddress,
                  address: {
                    line1: cardDetails.addressFirstLine,
                    line2: cardDetails.addressSecondLine,
                    city: cardDetails.city,
                    state: "",
                    country: cardDetails.country,
                    postal_code: cardDetails.postcode
                  }
                }
              });
            case 3:
              token = _context.sent;
              setIsStripeLoading(false);
              onSubmit.action(token.source.id);
            case 6:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));
    return function handleSubmit(_x) {
      return _ref9.apply(this, arguments);
    };
  }();
  var handleCardPayment = /*#__PURE__*/function () {
    var _ref10 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(intentSecret) {
      var _yield$stripe$handleC, error, paymentIntent;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return stripe.handleCardPayment(intentSecret);
            case 2:
              _yield$stripe$handleC = _context2.sent;
              error = _yield$stripe$handleC.error;
              paymentIntent = _yield$stripe$handleC.paymentIntent;
              if (error || !paymentIntent) onPaymentActionFail.action(error);else onPaymentActionSuccess.action();
            case 6:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));
    return function handleCardPayment(_x2) {
      return _ref10.apply(this, arguments);
    };
  }();
  var handleSetupIntent = /*#__PURE__*/function () {
    var _ref11 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(intentSecret) {
      return _regeneratorRuntime.wrap(function _callee3$(_context3) {
        while (1) {
          switch (_context3.prev = _context3.next) {
            case 0:
              _context3.next = 2;
              return stripe.handleCardSetup(intentSecret);
            case 2:
              onPaymentActionSuccess.action();
            case 3:
            case "end":
              return _context3.stop();
          }
        }
      }, _callee3);
    }));
    return function handleSetupIntent(_x3) {
      return _ref11.apply(this, arguments);
    };
  }();
  var handlePaymentAction = /*#__PURE__*/function () {
    var _ref12 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
      var action, intentSecret;
      return _regeneratorRuntime.wrap(function _callee4$(_context4) {
        while (1) {
          switch (_context4.prev = _context4.next) {
            case 0:
              action = paymentAction.action, intentSecret = paymentAction.intentSecret;
              _context4.t0 = action;
              _context4.next = _context4.t0 === "handle_card_payment" ? 4 : _context4.t0 === "handle_setup_intent" ? 7 : 10;
              break;
            case 4:
              _context4.next = 6;
              return handleCardPayment(intentSecret);
            case 6:
              return _context4.abrupt("break", 11);
            case 7:
              _context4.next = 9;
              return handleSetupIntent(intentSecret);
            case 9:
              return _context4.abrupt("break", 11);
            case 10:
              return _context4.abrupt("break", 11);
            case 11:
            case "end":
              return _context4.stop();
          }
        }
      }, _callee4);
    }));
    return function handlePaymentAction() {
      return _ref12.apply(this, arguments);
    };
  }();
  React.useEffect(function () {
    if (paymentAction) handlePaymentAction();
  }, [paymentAction]);
  React.useEffect(function () {
    if (onStripeLoadingUpdate) onStripeLoadingUpdate(isStripeLoading);
  }, [isStripeLoading]);
  return /*#__PURE__*/React__default.createElement(formik.Formik, {
    initialValues: initialValues,
    validationSchema: validation,
    onSubmit: handleSubmit
  }, function (props) {
    return /*#__PURE__*/React__default.createElement(Form, _extends({
      isLoading: isStripeLoading || isLoading,
      submitContent: onSubmit,
      cancelContent: onCancel
    }, props), children);
  });
};
var BillingForm$1 = reactStripeElements.injectStripe(BillingForm);

exports.BillingForm = BillingForm$1;
exports.CardDetails = CardDetails;
exports.StripeWrapper = StripeWrapper;
